import React from 'react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuCheckboxItem, Popover, PopoverContent, PopoverTrigger, Input, Button, Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@duck/ui'
import { Filter, X, Columns } from 'lucide-react'
import { TranslationKey, useTar } from '@/modules/i18n'
import type { EnumFilterOption } from './generalTable.utils'

interface TextFilterPopoverProps {
  label: TranslationKey
  labelT: (key: TranslationKey) => string
  filterValue: string
  hasFilter: boolean
  onFilterChange: (value: string) => void
  onClearFilter: () => void
}

export const TextFilterPopover: React.FC<TextFilterPopoverProps> = ({
  label,
  labelT,
  filterValue,
  hasFilter,
  onFilterChange,
  onClearFilter,
}) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`flex items-center justify-center h-4 w-4 hover:text-foreground transition-colors ${hasFilter ? 'text-accent' : 'text-muted-foreground/50'}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Filter className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="start" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">{labelT(label)}</label>
            {hasFilter && (
              <button
                onClick={onClearFilter}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Input
            type="text"
            placeholder={labelT('common.search')}
            value={filterValue || ''}
            onChange={(e) => onFilterChange(e.target.value)}
            className="w-full"
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface EnumFilterDropdownProps {
  label: TranslationKey
  labelT: (key: TranslationKey) => string
  enumOptions: EnumFilterOption[]
  filterArray: string[]
  hasFilter: boolean
  onToggleFilterItem: (value: string) => void
  onClearFilter: () => void
}

export const EnumFilterDropdown: React.FC<EnumFilterDropdownProps> = ({
  label,
  labelT,
  enumOptions,
  filterArray,
  hasFilter,
  onToggleFilterItem,
  onClearFilter,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex items-center justify-center h-4 w-4 hover:text-foreground transition-colors ${hasFilter ? 'text-accent' : 'text-muted-foreground/50'}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Filter className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-2 py-1.5">
          <span className="text-sm font-medium">{labelT(label)}</span>
          {hasFilter && (
            <button
              onClick={onClearFilter}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {enumOptions.map((option) => {
          const isChecked = filterArray?.includes(option.value) || false
          return (
            <DropdownMenuCheckboxItem
              key={option.value}
              checked={isChecked}
              onCheckedChange={() => onToggleFilterItem(option.value)}
              onSelect={(e) => e.preventDefault()}
            >
              {option.component || labelT(option.label)}
            </DropdownMenuCheckboxItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface ColumnVisibilityDropdownProps {
  availableColumns: Array<{ key: string; label: string }>
  isColumnVisible: (key: string) => boolean
  toggleColumnVisibility: (key: string) => void
  protectedColumnKeys?: string[]
}

export const ColumnVisibilityDropdown: React.FC<ColumnVisibilityDropdownProps> = ({
  availableColumns,
  isColumnVisible,
  toggleColumnVisibility,
  protectedColumnKeys = [],
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center justify-center h-5 w-5 hover:text-foreground transition-colors">
          <Columns className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {availableColumns.map((col) => {
          const isProtected = protectedColumnKeys.includes(col.key)
          return (
            <DropdownMenuCheckboxItem
              key={col.key}
              checked={isColumnVisible(col.key)}
              onCheckedChange={() => !isProtected && toggleColumnVisibility(col.key)}
              onSelect={(e) => e.preventDefault()}
              disabled={isProtected}
            >
              {col.label}
            </DropdownMenuCheckboxItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface ClearAllSettingsButtonProps {
  onClear: () => void
}

export const ClearAllSettingsButton: React.FC<ClearAllSettingsButtonProps> = ({
  onClear,
}) => {
  const { t } = useTar()
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button className="flex items-center justify-center h-5 w-5 hover:text-foreground transition-colors"
            onClick={onClear}
          >
            <X className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          {t('common.clearAllTableSettings')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
